import { motion, useReducedMotion, type HTMLMotionProps, type Transition } from 'framer-motion'
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { EASE_OUT } from '@shared/animations/consts'

export interface AnimatedHeightProps
  extends Omit<HTMLMotionProps<'div'>, 'animate' | 'children' | 'initial'> {
  children: React.ReactNode
  contentClassName?: string
  isVisible?: boolean
  withOpacity?: boolean
}

const defaultTransition: Transition = {
  height: { duration: 0.22, ease: EASE_OUT },
  opacity: { duration: 0.14, ease: 'easeOut' },
}

export const AnimatedHeight: React.FC<AnimatedHeightProps> = ({
  children,
  className,
  contentClassName,
  isVisible = true,
  style,
  transition,
  withOpacity = false,
  ...restProps
}) => {
  const prefersReducedMotion = useReducedMotion()
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number>()

  const updateHeight = useCallback(() => {
    const element = contentRef.current
    if (!element) {
      return
    }

    const nextHeight = Math.ceil(element.getBoundingClientRect().height)
    setHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight))
  }, [])

  useLayoutEffect(() => {
    updateHeight()

    const element = contentRef.current
    if (!element || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight)
      return () => window.removeEventListener('resize', updateHeight)
    }

    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)

    return () => observer.disconnect()
  }, [updateHeight])

  const animatedHeight = isVisible ? height ?? 'auto' : 0
  const motionTransition = prefersReducedMotion ? { duration: 0 } : transition ?? defaultTransition

  return (
    <motion.div
      className={className}
      initial={false}
      animate={{
        height: animatedHeight,
        opacity: withOpacity ? (isVisible ? 1 : 0) : 1,
      }}
      transition={motionTransition}
      style={{ overflow: 'hidden', ...style }}
      {...restProps}
    >
      <div ref={contentRef} className={contentClassName}>
        {children}
      </div>
    </motion.div>
  )
}
